import { useMemo, useState } from 'react'

import { Button } from '@/components/ui/Button'
import { Field, Input, Select } from '@/components/ui/Field'
import { Page, PageHeader, Section } from '@/components/ui/Page'
import { EmptyState, ErrorState, Loading } from '@/components/ui/States'
import { cn } from '@/lib/cn'
import { pluralise } from '@/lib/format'
import { describeError } from '@/lib/supabase'
import { useCurrentStaff } from '@/features/auth/RequireStaff'
import { useBoard } from '@/features/pipeline/queries'
import { TaskRow } from './TaskRow'
import { jobName } from '@/lib/jobs'
import { taskUrgency, useCreateTask, useTasks, useUpdateTask, type TaskWithRelations } from './queries'

/**
 * Her Trello lists, Today / Soon / Ideas, as three columns.
 *
 * Anything due today or earlier is pulled into Today whatever list it sits in,
 * so a dated task cannot hide in Soon past its date. Within a column, overdue
 * first, then by date, then undated.
 */

type Bucket = 'today' | 'soon' | 'ideas'

const COLUMNS: Array<{ key: Bucket; title: string; hint: string }> = [
  { key: 'today', title: 'Today', hint: 'Replies, chasing, anything for today.' },
  { key: 'soon', title: 'Soon', hint: 'Albums, USBs, systems to update.' },
  { key: 'ideas', title: 'Ideas', hint: 'For the business, one day.' },
]

function columnOf(task: TaskWithRelations): Bucket {
  if (task.bucket !== 'ideas') {
    const urgency = taskUrgency(task)
    if (urgency === 'overdue' || urgency === 'today') return 'today'
  }
  return task.bucket
}

export function TasksPage() {
  const { staff } = useCurrentStaff()
  const [scope, setScope] = useState<'open' | 'done'>('open')
  const query = useTasks(scope)
  const boardQuery = useBoard()
  const create = useCreateTask()
  const move = useUpdateTask()
  const [bucket, setBucket] = useState<Bucket>('today')

  const [title, setTitle] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [jobId, setJobId] = useState('')

  const tasks = query.data ?? []

  // Keyed on the query data, not the `?? []` fallback, which is a new array
  // literal on every render and would defeat the memo entirely.
  const grouped = useMemo(() => {
    const columns = new Map<Bucket, TaskWithRelations[]>(COLUMNS.map((column) => [column.key, []]))
    for (const task of query.data ?? []) columns.get(columnOf(task))?.push(task)
    for (const items of columns.values()) {
      items.sort((a, b) => (a.due_date ?? '9999').localeCompare(b.due_date ?? '9999') || a.sort_order - b.sort_order)
    }
    return columns
  }, [query.data])

  const overdue = tasks.filter((task) => taskUrgency(task) === 'overdue').length

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (!title.trim()) return

    await create.mutateAsync({
      title: title.trim(),
      due_date: dueDate || null,
      job_id: jobId || null,
      bucket,
      created_by: staff.id,
      assigned_to: staff.id,
    })

    setTitle('')
    setDueDate('')
    setJobId('')
  }

  return (
    <Page>
      <PageHeader
        eyebrow="Studio"
        title="Tasks"
        meta={
          query.data ? (
            <>
              <span>{pluralise(tasks.length, 'task')}</span>
              {overdue > 0 ? <span className="text-clay">{overdue} overdue</span> : null}
            </>
          ) : null
        }
      />

      <Section title="Add a task" className="border-t-[var(--border-hairline)]">
        <form onSubmit={(event) => void submit(event)} className="flex flex-wrap items-end gap-4">
          <Field label="What needs doing" className="min-w-[16rem] flex-1">
            <Input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Order the album for the Bekkers" />
          </Field>

          <Field label="Due" className="w-[10rem]">
            <Input type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} />
          </Field>

          <Field label="List" className="w-[8rem]">
            <Select value={bucket} onChange={(event) => setBucket(event.target.value as Bucket)}>
              {COLUMNS.map((column) => (
                <option key={column.key} value={column.key}>
                  {column.title}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Job" className="w-[14rem]">
            <Select value={jobId} onChange={(event) => setJobId(event.target.value)}>
              <option value="">Not about a job</option>
              {(boardQuery.data ?? []).map((job) => (
                <option key={job.id} value={job.id}>
                  {jobName(job)} — {job.event_date}
                </option>
              ))}
            </Select>
          </Field>

          <Button type="submit" variant="primary" loading={create.isPending} loadingLabel="Adding" disabled={!title.trim()}>
            Add task
          </Button>
        </form>
      </Section>

      <div className="mt-2 flex gap-0 border-b border-b-[var(--border-hairline)]">
        {(['open', 'done'] as const).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setScope(option)}
            aria-current={scope === option ? 'true' : undefined}
            className={cn(
              'label -mb-px border-b-2 px-3 py-2.5',
              scope === option ? 'border-b-accent font-semibold text-accent' : 'border-b-transparent hover:text-ink',
            )}
          >
            {option === 'open' ? 'Outstanding' : 'Done'}
          </button>
        ))}
      </div>

      {query.isPending ? (
        <Loading label="Loading tasks" />
      ) : query.isError ? (
        <div className="pt-6">
          <ErrorState title="Tasks could not load" detail={describeError(query.error)} onRetry={() => void query.refetch()} />
        </div>
      ) : tasks.length === 0 ? (
        <EmptyState
          className="mt-6"
          title={scope === 'open' ? 'Nothing outstanding' : 'Nothing finished yet'}
          body={
            scope === 'open'
              ? 'Everything is done. Add a task above, or one will appear when a wedding needs something.'
              : 'Completed tasks collect here.'
          }
        />
      ) : scope === 'done' ? (
        <ul className="mt-4 max-w-3xl">
          {tasks.map((task) => (
            <TaskRow key={task.id} task={task} />
          ))}
        </ul>
      ) : (
        <div className="mt-4 grid gap-8 lg:grid-cols-3">
          {COLUMNS.map((column) => {
            const items = grouped.get(column.key) ?? []
            return (
              <section key={column.key}>
                <h2 className="label mb-0.5">
                  {column.title} <span className="opacity-60">{items.length}</span>
                </h2>
                <p className="mb-2 text-[length:var(--text-2xs)] text-taupe">{column.hint}</p>
                <ul>
                  {items.map((task) => (
                    <TaskRow
                      key={task.id}
                      task={task}
                      onMove={(next) => move.mutate({ id: task.id, patch: { bucket: next } })}
                    />
                  ))}
                </ul>
              </section>
            )
          })}
        </div>
      )}
    </Page>
  )
}
